/**
 * Feature Flag Definitions
 *
 * Single source of truth for all feature flags in the Commander platform.
 * Types are automatically inferred from these constant definitions.
 *
 * To add a new feature flag:
 * 1. Add a new entry to FEATURE_FLAG_DEFINITIONS with full documentation
 * 2. Add a constant to FEATURE_FLAGS object
 * 3. TypeScript will automatically infer the types for you!
 *
 * @example
 * ```tsx
 * import { FEATURE_FLAGS } from '@commander/feature-flags';
 *
 * const isV2Enabled = useFeatureFlag(FEATURE_FLAGS.COMMS_V2);
 * ```
 */

/**
 * Feature flag metadata and configuration.
 * This is the single source of truth for all feature flags.
 */
export const FEATURE_FLAG_DEFINITIONS = {
  /**
   * V2 features across CRM and other apps
   *
   * When enabled, shows:
   * - Bulk selection functionality in Deals, Contacts, and Communications
   * - Refresh buttons in various components
   * - Updated UI components and workflows
   */
  v2: {
    key: 'v2',
    defaultValue: false,
    description: 'Enable V2 features across CRM and other apps',
  },
  /**
   * Deals V2 features
   *
   * When enabled, shows:
   * - Bulk selection functionality in Deals
   * - Refresh button in Deals module
   * - Updated UI components and workflows for deals
   */
  deals_v2: {
    key: 'deals_v2',
    defaultValue: false,
    description: 'Enable V2 features for Deals module',
  },
  /**
   * Communication Hub V2 features
   *
   * When enabled, shows:
   * - Schedule Meeting option in contacts
   * - Search bar in communications page
   * - Schedule Follow-up in call details
   * - Multi-select in email inbox/sent/drafts
   * - Archive button in SMS conversations
   * - Attachment button in SMS composer
   */
  comms_v2: {
    key: 'comms_v2',
    defaultValue: false,
    description: 'Enable V2 Communication Hub features',
  },
  /**
   * Analytics V2 features
   *
   * When enabled, shows:
   * - Activities goal type in Goals Modal
   * - Custom goal type in Goals Modal
   */
  analytics_v2: {
    key: 'analytics_v2',
    defaultValue: false,
    description: 'Enable Activities and Custom goal types in Analytics Goals Modal',
  },
  /**
   * HaulerCentral public directory feature
   *
   * When enabled, shows:
   * - HaulerCentral Profile menu item in user dropdown
   * - HaulerCentral Profile page (/haulercentral)
   */
  haulercentral: {
    key: 'haulercentral',
    defaultValue: false,
    description: 'Enable HaulerCentral public directory feature for waste haulers',
  },
  /**
   * Commission Tracking feature
   *
   * When enabled, shows:
   * - Commissions menu item in user dropdown
   * - Commission dashboard page (/commissions)
   * - Personal commission estimate card in deal details
   */
  commission_tracking: {
    key: 'commission_tracking',
    defaultValue: false,
    description: 'Enable commission tracking dashboard and deal estimates',
  },
  /**
   * Partner API documentation in Shell
   *
   * When enabled, shows:
   * - Partner API documentation entry point from API Access
   * - In-app documentation route for the partner integration guide
   */
  partner_api_docs: {
    key: 'partner_api_docs',
    defaultValue: false,
    description: 'Enable the in-app Partner API integration guide in Shell',
  },
  /**
   * Zapier integration entry point in CRM integrations tab
   *
   * When enabled, shows:
   * - Zapier card in the Connected Integrations tab
   * - Connect / Browse in Zapier link
   * - Connection state and Disconnect action
   * - "View setup guide" link and the in-app Zapier setup/app build guides
   *
   * Note: The backend also enforces a layered gate — both the user's
   * `zapier_integration` feature flag AND the company's `zapier_integration_enabled`
   * attribute must be true for a connection to succeed. See
   * verifyZapierEnabled in accessControl.js.
   */
  zapier_integration: {
    key: 'zapier_integration',
    defaultValue: false,
    description:
      'Enable the Zapier integration entry point in the CRM integrations tab, including the in-app setup and app build guides',
  },
  /**
   * Driver routing mobile companion app
   *
   * When enabled, shows:
   * - Driver Routing micro app access for provisioned driver users
   * - Driver app navigation entry points in shell surfaces
   * - Mobile execution flows backed by self-scoped driver APIs
   */
  routing_driver_app: {
    key: 'routing_driver_app',
    defaultValue: false,
    description: 'Enable the driver routing mobile micro app rollout',
  },
  // LIKELY DEAD: 2026-10-11 — read only by the unrouted TodayRoutePage; superseded by: unknown
  // — needs investigation. See docs/cleanup-backlog.md.
  /**
   * Driver routing map-first navigation
   *
   * When enabled, shows:
   * - Route preview maps in Today view
   * - Active in-app navigation map for the driver route screen
   * - Automatic arrival and navigation-driven stop execution flows
   */
  routing_driver_navigation_map: {
    key: 'routing_driver_navigation_map',
    defaultValue: false,
    description: 'Enable the map-first driver navigation experience in the driver routing app',
  },
  // LIKELY DEAD: 2026-10-11 — nothing gates on this flag; dispatch telematics is gated by
  // routing_driver_app; superseded by: routing_driver_app. See docs/cleanup-backlog.md.
  /**
   * Routing dispatch telematics monitoring
   *
   * When enabled, shows:
   * - Route tracking summary cards in dispatch route details
   * - Telematics alert states and vehicle assignment UX in dispatch
   * - Route-level telematics overlays in routing map experiences
   */
  routing_telematics_dispatch: {
    key: 'routing_telematics_dispatch',
    defaultValue: false,
    description: 'Enable webhook-driven telematics visibility in routing dispatch',
  },
  /**
   * Driver app passive telematics visibility
   *
   * When enabled, shows:
   * - Assigned vehicle connectivity banner in driver views
   * - Non-blocking telematics alert visibility for the assigned route
   * - Passive freshness information without changing navigation ownership
   */
  routing_telematics_driver_visibility: {
    key: 'routing_telematics_driver_visibility',
    defaultValue: false,
    description: 'Enable passive telematics visibility inside the driver routing app',
  },
  /*
   * Commander Go AI chat assistant
   *
   * When enabled, shows:
   * - Commander Go button in the global header
   * - AI chat panel for natural language data queries
   * - Translates questions into SQL queries against platform data
   */
  commander_go: {
    key: 'commander_go',
    defaultValue: false,
    description: 'Enable Commander Go AI chat assistant for natural language data queries',
  },
  /**
   * Hide Integrations feature
   *
   * When enabled, hides:
   * - Integrations tab in Profile Settings page
   */
  hide_integrations: {
    key: 'hide_integrations',
    defaultValue: false,
    description: 'Hide integrations tab from user profile settings',
  },
  /**
   * Universal Search v2
   *
   * When enabled, shows:
   * - ZoomInfo BYO connection UI in Integrations
   * - Universal Search contact targeting states
   */
  universal_search_v2: {
    key: 'universal_search_v2',
    defaultValue: false,
    description: 'Enable Universal Search v2 contact targeting and ZoomInfo connection UI',
  },
  /**
   * Tenant Admin Pipeline View
   *
   * When enabled, shows:
   * - "Pipeline View" tab on the Prospecting Analytics page (admin-only)
   * - Tenant-admin funnel cards, heatmap activity chart, and per-rep breakdown table
   */
  tenant_admin_pipeline_view: {
    key: 'tenant_admin_pipeline_view',
    defaultValue: false,
    description: 'Enable Pipeline View tab on Prospecting Analytics for tenant admins',
  },
  /**
   * Route IQ — route intelligence modal on the prospecting heatmap
   *
   * When enabled, shows:
   * - Route IQ modal when clicking a route card in the heatmap's right rail
   * - Demo metric cards (Density, Opportunity, Efficiency, Value) and a
   *   performance-over-time line chart populated from a backend endpoint
   */
  route_iq: {
    key: 'route_iq',
    defaultValue: false,
    description: 'Enable Route IQ intelligence modal for heatmap routes (demo data)',
  },
  /**
   * Prospecting Campaigns dashboard
   *
   * When enabled, shows:
   * - "Campaigns" nav entry + routes in the Prospecting app
   * - Tenant-scoped campaign list/detail, the per-campaign summary graph
   *   (Scheduled/Sent/Open/Reply over time), and campaign management for
   *   sales-rep-or-higher users
   */
  prospecting_campaigns: {
    key: 'prospecting_campaigns',
    defaultValue: false,
    description: 'Enable the tenant-scoped campaign dashboard in the Prospecting app',
  },
  /**
   * Prospecting Campaigns — dashboard-only mode
   *
   * When enabled (default), restricts the Prospecting Campaigns area to a
   * read-only dashboard:
   * - hides the "New campaign" button on the campaigns list page
   * - hides everything below the activity summary on the campaign detail page
   *   (authoring, variant performance, recipients, lifecycle actions)
   *
   * Disable per-tenant to expose full campaign authoring/management. Replaces
   * the hardcoded `SHOW_CAMPAIGN_DETAILS` demo gate.
   */
  prospecting_campaigns_dashboard_only: {
    key: 'prospecting_campaigns_dashboard_only',
    defaultValue: true,
    description:
      'Restrict Prospecting Campaigns to a read-only dashboard (hide create + detail authoring)',
  },
  /**
   * CRM account deep research / enrichment (Company Intelligence)
   *
   * When enabled, shows on the CRM accounts page:
   * - EnrichmentBadge on account rows (grid + table views)
   * - CompanyEnrichmentPanel sidebar (Enrich, Intelligence report) on row click
   *
   * Can be disabled per-company via the Admin app (crm_deep_research_enabled on Company).
   */
  crm_account_enrichment: {
    key: 'crm_account_enrichment',
    defaultValue: true,
    description: 'Enable CRM account deep research / enrichment (Company Intelligence) UI',
  },
  /**
   * Overture Places on the prospecting heatmap
   *
   * When enabled, shows:
   * - Overture Places points of interest in heatmap map search results
   * - The deck.gl density surface for searches whose match count is too large
   *   to render as individual pins
   *
   * Default OFF — the backing `features` row is seeded default-off and rolled
   * out per user. Google Places and the permit layers are unaffected either way.
   */
  overture_places_heatmap: {
    key: 'overture_places_heatmap',
    defaultValue: false,
    description: 'Overture Places points of interest in the heatmap map search',
  },
  /**
   * Signals opportunities and alerts
   *
   * When enabled:
   * - The Signals panel shows on CRM account pages and prospecting prospect pages
   * - The opportunity detail view at `signals/opportunities/:id` is routed
   * - The Signals actions are offered: create prospect, attach, assign and the
   *   wrong-business and wrong-size marks
   * - The rep receives Signals opportunity alerts in the notification bell and
   *   by email; each alert links to the opportunity detail view
   * - The Signals tab in CRM Settings, where a tenant admin sets the alert
   *   limits and default channels for the company and each territory
   * - Signals opportunity cards in Action Dash, one per open opportunity the
   *   rep owns or is addressed by, and the feed's "Signals" type filter
   *
   * Default OFF — the backing `features` row is seeded default-off and rolled
   * out per tenant or user. Alerts are still evaluated and recorded for reps
   * with the flag off; they are just not sent. The admin operator console is
   * gated by the super-admin check, not this flag.
   */
  signals: {
    key: 'signals',
    defaultValue: false,
    description:
      'Signals opportunities and alerts: the detail view, the account and prospect panel, the Signals actions, alerts in the bell and by email, and the Signals settings tab in CRM Settings',
  },
  /**
   * Voice profile generator (#2240)
   *
   * When enabled, shows:
   * - The "Match my writing style" affordance in communication settings, where a
   *   rep pastes 2-5 emails they wrote, generates a style profile, edits it, and
   *   applies it to their custom communication instructions
   *
   * Default OFF — new behaviour with no eval behind it yet, rolled out one
   * tenant at a time.
   *
   * Gates profile CREATION only. An applied profile lands in
   * `communication_style_text`, which every generation path reads including
   * prospecting autopilot; turning the flag off does not withdraw it.
   */
  voice_profile_generator: {
    key: 'voice_profile_generator',
    defaultValue: false,
    description: 'Generate a writing-style profile from pasted emails in communication settings',
  },
  /**
   * CMS medical signals on the prospecting heatmap
   *
   * When enabled, shows:
   * - CMS_POS pins (Medicare/Medicaid-certified care facilities) in heatmap
   *   map search results
   * - CMS_CLIA pins (clinical labs) in the same results
   * - Both layers in the Tools panel's permit-layer toggles
   *
   * Gated entirely in the backend fan-out: a tenant without the flag, or
   * without a healthcare ICP (NAICS 62), never receives these pins, so there
   * is no frontend gate. Default OFF — rolled out per tenant.
   */
  cms_medical_heatmap: {
    key: 'cms_medical_heatmap',
    defaultValue: false,
    description: 'CMS medical facility and clinical-lab pins in the heatmap map search',
  },
  /**
   * Global / Universal Search
   *
   * When enabled, shows:
   * - The universal search bar in app headers
   * - The search button / input in app sidebar headers
   * - The ⌘/ keyboard shortcut to open the search dialog
   * - The GlobalSearchCommand dialog where it is implemented
   *
   * Results come from the backend Typesense search (`GET /search`). Default OFF —
   * rolled out per tenant.
   */
  global_search: {
    key: 'global_search',
    defaultValue: false,
    description: 'Enable global search / universal search entry points across supported apps',
  },
  /**
   * Global Search v2 (company and people discovery)
   *
   * When enabled, shows:
   * - The Global Search entry in the app switcher
   * - The Global Search page: new companies and people in the rep's metro,
   *   with targeting into Commander
   *
   * The backend rejects every `/global-search` route while it is off. Separate
   * from `global_search` (v1's header search bar) so the two switch independently.
   * Default OFF — rolled out per tenant.
   */
  global_search_v2: {
    key: 'global_search_v2',
    defaultValue: false,
    description: 'Global Search v2: company and people discovery with targeting',
  },
  /**
   * Global Search industry filter
   *
   * When enabled, shows in the Global Search filter panel:
   * - The Industry picker, which narrows company rows by each company's HQ NAICS
   * - Its applied-filter chips
   *
   * Off, the backend ignores any NAICS codes a search carries. Separate from
   * `global_search_v2` so it rolls out to tenants that already have Global Search.
   * Default OFF — rolled out per tenant.
   */
  global_search_naics_filter: {
    key: 'global_search_naics_filter',
    defaultValue: false,
    description: 'Global Search industry filter: narrow company results by HQ NAICS',
  },
  /**
   * Commander Lists (saved account buckets)
   *
   * When enabled, shows in the CRM:
   * - The Lists entry in the sidebar
   * - The /crm/lists route (index + rule builder)
   *
   * Default OFF — not because it is unfinished, but because it has not been
   * rolled out to a tenant yet. The backend is live (lists/list_members, the
   * rule compiler, CSV import/export, member management) and the mock layer is
   * gone. See dev/active/lists-frontend/.
   */
  lists: {
    key: 'lists',
    defaultValue: false,
    description: 'Enable Commander Lists (saved account buckets) in the CRM',
  },
  /**
   * Commander Sequences (automated follow-up)
   *
   * When enabled, shows in the CRM:
   * - The Sequences entry in the sidebar
   * - The /crm/sequences route (index + enrollment rule + pre-flight)
   *
   * Default OFF — not a stub. The step builder, the dispatcher, AI drafting,
   * branching and the reply/bounce stop machine are all built; what is missing
   * is a tenant rollout. Both schedulers run in every deployed environment, and
   * the dispatcher asks this flag of the sequence owner and the frozen sender
   * before each send, so turning it on for a user starts real sending. The
   * backend routes are gated on it too. Independent of the `lists` flag: the
   * list endpoints and the rule builder a sequence audience reuses are not
   * behind it, so a tenant can run sequences without the Lists index.
   * See dev/active/lists-frontend/.
   */
  sequences: {
    key: 'sequences',
    defaultValue: false,
    description: 'Enable Commander Sequences (automated follow-up) in the CRM',
  },
  /**
   * Redesigned CRM Company Detail page (#2689)
   *
   * When enabled, /crm/accounts/:id renders the two-column record page (fact
   * strip, deals, activity with the recommended next action, and a sticky
   * rail for details, contacts, sites and documents) instead of the tabbed
   * layout.
   *
   * Default OFF — opt-in per user or tenant until the pilot signs off. UI-only:
   * no backend route is gated on it.
   */
  crm_company_detail_v2: {
    key: 'crm_company_detail_v2',
    defaultValue: false,
    description: 'Redesigned two-column Company Detail page in the CRM',
  },
  /**
   * AI account summary on the Company Detail page (#2689)
   *
   * When enabled (and `crm_company_detail_v2` is on), the page shows the
   * account summary card, and the backend serves and refreshes it. Refreshing
   * runs a paid model call, so the routes are gated by this flag too.
   *
   * Default OFF — it spends money per refresh, so it stays a separate switch
   * from the layout flag and is opted into per user or tenant.
   */
  crm_account_summary_preview: {
    key: 'crm_account_summary_preview',
    defaultValue: false,
    description: 'AI account summary card and its endpoints on the CRM Company Detail page',
  },
  /**
   * Web Capture
   *
   * When enabled, shows:
   * - The Web Capture section in the CRM sidebar
   * - Submissions — the inbox every widget submission lands in for triage
   * - Cases — the queue approved service requests become
   * - Widgets and the widget builder at /crm/web-capture/widgets
   *
   * Default OFF — the whole feature is new and unreleased, so it is opted into
   * per tenant. The flag also gates the authenticated widget and case API, but
   * not the public submission endpoint: a widget already live on a hauler's
   * site keeps accepting submissions whatever this says.
   */
  widget_builder: {
    key: 'widget_builder',
    defaultValue: false,
    description: 'Web Capture — the website widget builder, Submissions inbox, and Cases',
  },
  /**
   * Heatmap ICP fence
   *
   * When enabled for a company, heatmap searches and prospect writes are
   * restricted to the locations on the user's resolved ICP:
   * - The chat tools `map_search` and `add_to_map` decline out-of-area work
   * - The map UI search and density endpoints refuse out-of-area coordinates
   *   and filter spillover results
   * - `POST /heatmap/engage` and `POST /heatmap/add_to_queue` reject records
   *   outside the fence
   *
   * Default OFF — it restricts behaviour that is live today, so it is opted
   * into per tenant. A user whose resolved ICP has no usable locations is not
   * fenced at all. No frontend gate: enforcement is entirely server-side.
   */
  heatmap_icp_fence: {
    key: 'heatmap_icp_fence',
    defaultValue: false,
    description: "Fence heatmap searches and prospect writes to the user's ICP locations",
  },
  /**
   * Heatmap ICP fence bypass
   *
   * When enabled for an individual user, that user is exempt from an otherwise
   * active `heatmap_icp_fence`. Only meaningful as a per-user override set by
   * Commander staff in the admin app — there is no in-product grant path, and
   * no role bypasses the fence without it.
   *
   * Default OFF.
   */
  heatmap_icp_fence_bypass: {
    key: 'heatmap_icp_fence_bypass',
    defaultValue: false,
    description: 'Exempt an individual user from an active heatmap ICP fence',
  },
  /**
   * Automatic prospect push to the assignee's CRM
   *
   * When enabled, a prospect that becomes both approved and fully enriched is
   * automatically activated (moved from recommended to targeting) and pushed to
   * its assignee's connected CRM through the existing push pipeline — no "Add
   * to CRM" click.
   *
   * Backend-only gate (the guard and the push worker); there is no new UI, so
   * this registry entry exists for the admin catalog and for parity with the
   * backend default.
   *
   * Default OFF — opted into per tenant or per user. It applies only to
   * lifecycle events that occur after enablement: turning it on does not push
   * the tenant's existing approved and enriched backlog.
   */
  auto_push_prospects_to_crm: {
    key: 'auto_push_prospects_to_crm',
    defaultValue: false,
    description:
      "Automatically push approved, fully enriched prospects to the assignee's CRM — applies only to lifecycle events after enablement, with no backfill of the existing backlog",
  },
  /**
   * Quote territory visibility
   *
   * When enabled, a SALES_REP sees every quote in their assigned territory
   * (including teammates' drafts) instead of only quotes they personally
   * prepared — matching how deals/accounts/contacts already work.
   *
   * Backend-only gate (quote list/detail/write/approval paths and quote
   * analytics); there is no new UI, so this registry entry exists for the
   * admin catalog and for parity with the backend default.
   *
   * Default OFF — opted into per tenant.
   */
  quote_territory_visibility: {
    key: 'quote_territory_visibility',
    defaultValue: false,
    description:
      'Let a sales rep see every quote in their assigned territory, not just quotes they personally prepared',
  },
  /**
   * Email Analysis
   *
   * When enabled, shows in the CRM:
   * - The Email Analysis entry in the sidebar
   * - The /crm/communication/email-analysis route — companies with email
   *   history, per-email sentiment, objections, buying signals and coaching
   * - The AI chat over a company's email history, and manual email add /
   *   re-analyze
   *
   * The same flag gates every route in the backend's emailAnalysis.routes.js
   * and the automatic analysis of inbound email, which is the expensive half:
   * with it off, no inbound email is sent to an LLM at all.
   *
   * Default OFF — opted into per tenant as haulers are onboarded.
   */
  email_analysis: {
    key: 'email_analysis',
    defaultValue: false,
    description:
      'AI-powered Email Analysis in the CRM: per-company email insights, chat, and automatic inbound analysis',
  },
  /**
   * V2 contact-enrichment pipeline
   *
   * Backend-only, like `cms_medical_heatmap` and `auto_push_prospects_to_crm`: there is no
   * UI and no frontend gate. It selects which pipeline a tenant's contact enrichment runs
   * through, read fail-closed on the worker path in
   * `services/enrichment/contactPipelineFlag.js` and snapshotted for the life of one run.
   *
   * It is registered here so the key exists in the one place the platform treats as the
   * authoritative list of flags — omitting it is the `universal_search_v2` drift in reverse.
   *
   * Default OFF. There is no shadow phase, so the first old-versus-new comparison happens on
   * live prospects; a tenant is opted in deliberately, and flipping the tenant override back
   * off is the whole rollback.
   */
  enrichment_v3: {
    key: 'enrichment_v3',
    defaultValue: false,
    description: "Run this tenant's contact enrichment through the v3 pipeline",
  },
  /**
   * Ideal Customer Profile Blueprint in the Prospecting app
   *
   * When enabled, shows for a company ADMIN or MANAGER:
   * - the Blueprint card on the Command Center dashboard
   * - the /prospecting/command-center/blueprint page, where the analyst
   *   researches the company, interviews the manager, and writes their brief
   *
   * The same flag gates the backend's `/prospecting-blueprint/*` routes and the
   * paid model turns behind them, so it is the per-tenant opt-in for a feature
   * that spends model budget on the tenant's behalf. Commander's own internal
   * Blueprint on the admin side is a separate surface and is not gated by it.
   *
   * Default OFF — turned on company-wide for a hauler that has been onboarded.
   */
  prospecting_blueprint: {
    key: 'prospecting_blueprint',
    defaultValue: false,
    description: 'The Ideal Customer Profile Blueprint in the prospecting app',
  },
  /**
   * Calendar attendee linkage
   *
   * When enabled, calendar sync links meetings to accounts by attendee
   * instead of only manually-linked meetings. Gates the Sequences builder's
   * "Meeting logged on the account" exit criterion, which otherwise has
   * nothing reliable to check against.
   *
   * Default OFF. Read per mailbox owner by the calendar linkage service, and
   * by the Sequences settings rail for the meeting exit.
   */
  calendar_attendee_linkage: {
    key: 'calendar_attendee_linkage',
    defaultValue: false,
    description:
      'Calendar sync links meetings to accounts; enables the meeting-logged sequence exit',
  },
  /**
   * Link-click tracking for Sequences
   *
   * When enabled, shows:
   * - The "Track link clicks" switch in the sequence settings rail
   * - The "Clicked a link in the previous email" and "Clicked any link" branch conditions
   * - The Competitive Displacement starter template, which branches on it
   *
   * The tracking code has shipped: turning this on lets the owner switch link
   * tracking on for a sequence, which rewrites that sequence's outgoing links
   * through Commander so a click can be branched on. Off, a stored switch
   * reads and runs as off and no email is rewritten.
   *
   * Default OFF.
   */
  sequence_click_tracking: {
    key: 'sequence_click_tracking',
    defaultValue: false,
    description:
      'Link-click tracking for Sequences: the per-sequence tracking switch, the clicked-link branch, and the template that uses it',
  },
  /**
   * Account-owner sending for Sequences
   *
   * When enabled, shows:
   * - The "Send from each account's owner" setting in the sequence settings
   *   rail, for ADMIN and MANAGER roles
   * - The Dormant Account Reactivation starter template defaulting to it
   *
   * The sending code has shipped: with this on, an admin or manager can set a
   * sequence to send from each account's owner, and the "account is reassigned"
   * exit is forced on with it. Off, a stored ACCOUNT_OWNER reads and enrolls as
   * the sequence owner; runs already enrolled keep the sender they froze.
   *
   * Default OFF.
   */
  sequence_sender_mode: {
    key: 'sequence_sender_mode',
    defaultValue: false,
    description:
      "Send each account's sequence mail from that account's owner (admins and managers only), and the template that defaults to it",
  },
  /**
   * Hide Field Capture filter
   *
   * When enabled, hides:
   * - The "Field Capture" option in the prospecting Source filter dropdown
   *
   * Default ON — there is no mobile app yet producing field_capture-sourced
   * prospects, so the filter option is hidden on web everywhere. Disable per
   * tenant once their mobile rollout goes live.
   */
  hide_field_capture_filter: {
    key: 'hide_field_capture_filter',
    defaultValue: true,
    description: 'Hide the Field Capture source filter option in the web prospecting UI',
  },
  /**
   * Action Dash home feed
   *
   * When enabled, displays:
   * - The Action Dash home feed in place of the standard /dashboard page
   *
   * Default OFF.
   */
  action_dash: {
    key: 'action_dash',
    defaultValue: false,
    description: 'Action Dash home feed replacing /dashboard',
  },
  /**
   * Prospect recommendations
   *
   * Backend only; nothing in the UI reads it. The Action Dash feed leaves out the
   * "Prospects recommended for you" card for a rep whose team has it off.
   *
   * Default ON — the card is live for every rep; this only lets a company switch it off.
   */
  prospect_recommendations: {
    key: 'prospect_recommendations',
    defaultValue: true,
    description: 'Daily "Prospects recommended for you" card in the Action Dash feed',
  },
  /**
   * Redesigned CRM Quotes list (#3054)
   *
   * When enabled, displays:
   * - The grid table layout with configurable density
   * - Columns menu for customizing column visibility
   * - Redesigned filter panel with search and filter chips
   * - Draft-only bulk delete action
   * - Hides the four stat cards
   *
   * When disabled, renders the previous Quotes list (QuotesListLegacy).
   *
   * Default OFF — opted in per company or user from the admin app.
   */
  crm_quotes_redesign: {
    key: 'crm_quotes_redesign',
    defaultValue: false,
    description: 'Redesigned CRM Quotes list (#3054) replacing the previous list',
  },
  /**
   * Prospecting territory contact ladder
   *
   * Backend only; nothing in the UI reads it. When enabled for a rep:
   * - The prospect generator's contact search widens site → 25 mi → 50 mi → state
   *   instead of falling back nationwide
   * - A contact found outside the rep's ICP ZIPs no longer moves the lead's address;
   *   the lead keeps the searched site and is rejected with the contact persisted
   *
   * Default OFF — rolled out as a per-user override one rep at a time.
   */
  prospecting_territory_contact_ladder: {
    key: 'prospecting_territory_contact_ladder',
    defaultValue: false,
    description:
      "Generated leads search for contacts site → 25 mi → 50 mi → state and keep the searched site when the contact is outside the rep's territory",
  },
  /**
   * Quote haul price
   *
   * Backend only; nothing in the UI reads it. When enabled for a rep, the
   * roll-off quotes they create in the disposal-rate layout print a Haul
   * column before Surcharges on the PDF and the review tab. The decision is
   * frozen onto each quote at creation, so the review tab reads the quote,
   * not this flag.
   *
   * Default OFF — rolled out as a per-user override.
   */
  quote_show_haul_price: {
    key: 'quote_show_haul_price',
    defaultValue: false,
    description:
      'Roll-off quotes this user creates in the disposal-rate layout show a Haul column before Surcharges',
  },
  /**
   * Legacy quote builder
   *
   * When enabled for a tenant, its users keep today's quote builder, quote
   * details, line editors, and context editors, and its admins create rate
   * cards on the legacy engines only. Off, the tenant builds authored pricing
   * models in the new builder.
   *
   * Tenant-only: the backend ignores user overrides of this key, so every
   * employee of a company reads the company's value.
   *
   * Default OFF — turned on by migration for every tenant that existed when it
   * shipped; a tenant created later starts on authored pricing models.
   */
  legacy_quote_builder: {
    key: 'legacy_quote_builder',
    defaultValue: false,
    description:
      "Keep this tenant on today's quote builder and legacy-engine rate cards. Tenant-only: user overrides are ignored",
  },
  /**
   * CRM Contacts redesign (#3056)
   *
   * When enabled, displays:
   * - The redesigned /crm/contacts table and contact details panel
   *
   * Default OFF: users without it get the previous design.
   */
  crm_contacts_refresh: {
    key: 'crm_contacts_refresh',
    defaultValue: false,
    description:
      'Redesigned CRM Contacts page, contact details panel, and Accounts page tag filter',
  },
} as const;

/**
 * Convenient constants for accessing feature flags.
 * Use these instead of raw strings to get autocomplete and type safety.
 */
export const FEATURE_FLAGS = Object.fromEntries(
  Object.keys(FEATURE_FLAG_DEFINITIONS).map((key) => [
    key.toUpperCase(),
    FEATURE_FLAG_DEFINITIONS[key as keyof typeof FEATURE_FLAG_DEFINITIONS].key,
  ])
) as {
  [K in keyof typeof FEATURE_FLAG_DEFINITIONS as Uppercase<
    K & string
  >]: (typeof FEATURE_FLAG_DEFINITIONS)[K]['key'];
};

/**
 * Type helper to extract feature flag keys
 */
export type FeatureFlagKey = keyof typeof FEATURE_FLAG_DEFINITIONS;

/**
 * Type helper to extract feature flag constant values
 */
export type FeatureFlagConstant = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
